import { useState } from 'react'
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiPost } from '../lib/api.ts'
import { BUTTON, ErrorNote, INPUT, LABEL, useIssueState } from '../lib/admin.tsx'
import {
  activityById,
  ago,
  CampaignTable,
  money,
  shortDate,
  Spark,
  Status,
  Live,
  useAdminCampaigns,
  useAgreements,
  useOpenWorkspace,
  useToday,
  type AdminCampaign,
  type Agreement,
} from '../lib/platform.tsx'
import { isSuperAdmin } from '../lib/roles.ts'
import { useRefreshSession, useSession } from '../lib/session.ts'
import { Overview } from './Overview.tsx'
import { Snapshots } from './Snapshots.tsx'
import { TeamPanel } from './Team.tsx'

/**
 * The Platform area: Today (its own file), Customers → a customer → a
 * campaign's support page, Billing, People, Voter data — for SuperAdmin and Org
 * Admins (docs/decisions.md 2026-09-30, reshaped 2026-10-01 from the mockup
 * the owner approved). Every read and write here is refused by the database
 * for anyone else (0085); these screens only exist in the menu for platform
 * admins.
 *
 * "Customer" is campaign.org: a consultant firm, or a single campaign buying
 * on its own.
 */

interface Customer {
  org_id: string
  name: string
  status: string
  campaigns: number
  open_campaigns: number
}

function useCustomers() {
  return useQuery({
    queryKey: ['admin-customers'],
    queryFn: () => apiGet<{ customers: Customer[] }>('/admin/customers'),
  })
}

const GHOST = 'rounded-md border border-brand-600 px-3 py-1.5 text-sm font-semibold text-brand-700 hover:bg-brand-50 disabled:opacity-50'

/* ------------------------------------------------------------------ Customers */

export function Customers() {
  const client = useQueryClient()
  const navigate = useNavigate()
  const { data: session } = useSession()
  const superAdmin = isSuperAdmin(session)
  const customers = useCustomers()
  const campaigns = useAdminCampaigns()
  const agreements = useAgreements()
  const today = useToday()
  const { error, setError, report } = useIssueState()
  const [adding, setAdding] = useState(false)
  const [name, setName] = useState('')

  const create = useMutation({
    mutationFn: () => apiPost<{ org_id: string }>('/admin/customers', { name: name.trim() }),
    onSuccess: async (r) => {
      setError(null)
      setName('')
      await client.invalidateQueries({ queryKey: ['admin-customers'] })
      await navigate({ to: '/platform/customers/$orgId', params: { orgId: r.org_id } })
    },
    onError: report,
  })

  const rows = customers.data?.customers ?? []
  const activity = activityById(today.data?.activity)
  const active = (orgId: string) =>
    (campaigns.data?.campaigns ?? [])
      .filter((c) => c.org_id === orgId)
      .reduce((n, c) => n + (activity.get(c.campaign_id)?.calling_now ?? 0) + (activity.get(c.campaign_id)?.walking_now ?? 0), 0)
  // Booked this cycle: live agreements only. Prices come back only for the
  // SuperAdmin (list_agreements), so the column exists only for them.
  const booked = (orgId: string) =>
    (agreements.data?.agreements ?? [])
      .filter((a) => a.org_id === orgId && a.status === 'active')
      .reduce((n, a) => n + Number(a.price_cents ?? 0), 0)

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Customers</h1>
          <p className="mt-1 max-w-prose text-sm text-ink-muted">
            Who you sell to: a consultant firm, an organization, or a single campaign.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" className={GHOST} onClick={() => setAdding(!adding)}>New customer</button>
          <Link to="/platform/one-off" className={BUTTON}>Get a customer started</Link>
        </div>
      </div>

      {adding ? (
        <section className="flex flex-col gap-3 rounded-lg border border-brand-200 bg-brand-50 p-4">
          <h2 className="text-sm font-semibold">New customer</h2>
          <ErrorNote error={error} />
          <div className="flex flex-wrap items-end gap-3">
            <label className="text-sm">
              <span className={LABEL}>Name</span>
              <input id="new-customer-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Coastline Strategies" maxLength={200} className={`w-72 max-w-full ${INPUT}`} />
            </label>
            <button type="button" disabled={!name.trim() || create.isPending} onClick={() => create.mutate()} className={BUTTON}>
              {create.isPending ? 'Creating…' : 'Create customer'}
            </button>
            <button type="button" className="text-sm text-ink-muted hover:text-ink" onClick={() => setAdding(false)}>Cancel</button>
          </div>
        </section>
      ) : null}

      <div className="overflow-x-auto rounded-lg border border-line">
        <table className="w-full min-w-[34rem] border-collapse text-sm">
          <thead className="bg-surface-sunken">
            <tr className="text-left text-xs tracking-wide text-ink-muted uppercase">
              <th scope="col" className="py-2 pr-4 pl-3 font-semibold">Customer</th>
              <th scope="col" className="py-2 pr-4 font-semibold">Campaigns</th>
              <th scope="col" className="py-2 pr-4 font-semibold">Right now</th>
              {superAdmin ? <th scope="col" className="py-2 pr-3 text-right font-semibold">Booked</th> : null}
            </tr>
          </thead>
          <tbody>
            {customers.isPending ? (
              <tr><td colSpan={4} className="px-3 py-4 text-ink-muted">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={4} className="px-3 py-6 text-ink-muted">No customers yet.</td></tr>
            ) : (
              rows.map((c) => {
                const n = active(c.org_id)
                return (
                  <tr key={c.org_id} className="border-t border-line hover:bg-surface-sunken/60">
                    <td className="py-2 pr-4 pl-3">
                      <Link to="/platform/customers/$orgId" params={{ orgId: c.org_id }} className="font-semibold text-brand-700 hover:underline">
                        {c.name}
                      </Link>
                      {c.status !== 'active' ? <span className="ml-2 text-xs text-ink-muted">{c.status}</span> : null}
                    </td>
                    <td className="py-2 pr-4 tabular-nums">
                      {c.campaigns} <span className="text-xs text-ink-muted">({c.open_campaigns} open)</span>
                    </td>
                    <td className="py-2 pr-4"><Live on={n > 0}>{n > 0 ? `${n} working` : 'Quiet'}</Live></td>
                    {superAdmin ? <td className="py-2 pr-3 text-right tabular-nums">{money(booked(c.org_id))}</td> : null}
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export function CustomerPage() {
  const { orgId } = useParams({ from: '/platform/customers/$orgId' })
  const customers = useCustomers()
  const campaigns = useAdminCampaigns(orgId)
  const today = useToday()
  const customer = customers.data?.customers.find((c) => c.org_id === orgId)
  const [form, setForm] = useState<'campaign' | 'agreement' | null>(null)
  const list = campaigns.data?.campaigns ?? []

  return (
    <div className="flex flex-col gap-5">
      <div>
        <p className="text-xs text-ink-muted">
          <Link to="/platform/customers" className="hover:underline">Customers</Link> ›
        </p>
        <div className="mt-1 flex flex-wrap items-end justify-between gap-3">
          <h1 className="text-xl font-semibold tracking-tight">{customer?.name ?? '…'}</h1>
          <div className="flex flex-wrap gap-2">
            <button type="button" className={GHOST} onClick={() => setForm(form === 'agreement' ? null : 'agreement')}>New agreement</button>
            <button type="button" className={BUTTON} onClick={() => setForm(form === 'campaign' ? null : 'campaign')}>New campaign</button>
          </div>
        </div>
      </div>

      {form === 'campaign' ? <NewCampaignForm orgId={orgId} onCancel={() => setForm(null)} /> : null}
      {form === 'agreement' ? (
        list.length ? (
          <NewAgreementForm orgId={orgId} campaigns={list} onDone={() => setForm(null)} />
        ) : (
          <p className="text-sm text-ink-muted">Add a campaign first. An agreement covers one or more campaigns.</p>
        )
      ) : null}

      <section className="flex flex-col gap-2">
        <h2 className="font-semibold">Campaigns</h2>
        <CampaignTable campaigns={list} activity={activityById(today.data?.activity)} showCustomer={false} />
      </section>

      <section className="flex flex-col gap-2">
        <h2 className="font-semibold">What they bought</h2>
        <p className="max-w-prose text-sm text-ink-muted">
          Licenses on an agreement are shared by every campaign on it. Whoever signs in first takes a free one.
        </p>
        <AgreementList orgId={orgId} campaigns={list} />
      </section>
    </div>
  )
}

function NewCampaignForm({ orgId, onCancel }: { orgId: string; onCancel: () => void }) {
  const client = useQueryClient()
  const navigate = useNavigate()
  const { error, setError, report } = useIssueState()
  const [f, setF] = useState({ name: '', election_date: '', attestation_document: '', attestation_signed_at: '', s19012_notified_at: '' })
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value })

  const create = useMutation({
    mutationFn: () =>
      apiPost<{ campaign_id: string }>('/admin/campaigns', {
        org_id: orgId,
        name: f.name.trim(),
        election_date: f.election_date,
        attestation_document: f.attestation_document.trim() || null,
        attestation_signed_at: f.attestation_signed_at || null,
        s19012_notified_at: f.s19012_notified_at || null,
      }),
    onSuccess: async (r) => {
      setError(null)
      await client.invalidateQueries({ queryKey: ['admin-campaigns'] })
      await client.invalidateQueries({ queryKey: ['admin-customers'] })
      await navigate({ to: '/platform/campaigns/$campaignId', params: { campaignId: r.campaign_id } })
    },
    onError: report,
  })

  // An attestation needs both halves: where it is, and when it was signed.
  const halfAttested = Boolean(f.attestation_document.trim()) !== Boolean(f.attestation_signed_at)

  return (
    <section className="rounded-lg border border-brand-200 bg-brand-50 p-4">
      <h2 className="text-sm font-semibold">New campaign for this customer</h2>
      <ErrorNote error={error} />
      <CampaignFields f={f} set={set} />
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <button
          type="button"
          disabled={!f.name.trim() || !f.election_date || halfAttested || create.isPending}
          onClick={() => create.mutate()}
          className={BUTTON}
        >
          {create.isPending ? 'Creating…' : 'Create campaign'}
        </button>
        <button type="button" className="text-sm text-ink-muted hover:text-ink" onClick={onCancel}>Cancel</button>
        <span className="text-xs text-ink-muted">
          Without its signed attestation a campaign can be set up but nobody can use it yet.
        </span>
      </div>
    </section>
  )
}

function CampaignFields({
  f,
  set,
}: {
  f: { name: string; election_date: string; attestation_document: string; attestation_signed_at: string; s19012_notified_at: string }
  set: (k: 'name' | 'election_date' | 'attestation_document' | 'attestation_signed_at' | 's19012_notified_at') => (e: { target: { value: string } }) => void
}) {
  return (
    <div className="mt-3 grid gap-3 sm:grid-cols-2">
      <label className="text-sm">
        <span className={LABEL}>Campaign name</span>
        <input id="campaign-name" value={f.name} onChange={set('name')} maxLength={200} placeholder="Alvarez for Assembly" className={`w-full ${INPUT}`} />
      </label>
      <label className="text-sm">
        <span className={LABEL}>Election day</span>
        <input id="campaign-election" type="date" value={f.election_date} onChange={set('election_date')} className={`w-full ${INPUT}`} />
      </label>
      <label className="text-sm">
        <span className={LABEL}>Signed attestation — where it is filed</span>
        <input
          id="campaign-attestation-doc"
          value={f.attestation_document}
          onChange={set('attestation_document')}
          maxLength={500}
          placeholder="Drive: Attestations/2026/alvarez.pdf"
          className={`w-full ${INPUT}`}
        />
      </label>
      <label className="text-sm">
        <span className={LABEL}>Attestation signed on</span>
        <input id="campaign-attestation-date" type="date" value={f.attestation_signed_at} onChange={set('attestation_signed_at')} className={`w-full ${INPUT}`} />
      </label>
      <label className="text-sm">
        <span className={LABEL}>SOS vendor notice sent on, §19005(c) (optional)</span>
        <input id="campaign-19012" type="date" value={f.s19012_notified_at} onChange={set('s19012_notified_at')} className={`w-full ${INPUT}`} />
      </label>
    </div>
  )
}

/* ------------------------------------------------------------------ Campaigns */

export function PlatformCampaigns() {
  const campaigns = useAdminCampaigns()
  const today = useToday()
  const activity = activityById(today.data?.activity)
  // Busiest first, as on Today.
  const rows = [...(campaigns.data?.campaigns ?? [])].sort((x, y) => {
    const n = (id: string) => {
      const a = activity.get(id)
      return a ? (a.calling_now + a.walking_now) * 1000 + a.walk_today + a.phone_today : 0
    }
    return n(y.campaign_id) - n(x.campaign_id)
  })
  return (
    <div className="flex flex-col gap-4">
      <div>
        <h1 className="text-xl font-semibold tracking-tight">Campaigns</h1>
        <p className="mt-1 max-w-prose text-sm text-ink-muted">
          Every campaign on the platform, busiest first. Open one to help them: add people, add
          licenses, check paperwork.
        </p>
      </div>
      <CampaignTable campaigns={rows} activity={activity} showCustomer />
    </div>
  )
}

const TABS = [
  { key: 'team', label: 'Team' },
  { key: 'licenses', label: 'Licenses' },
  { key: 'paperwork', label: 'Paperwork' },
] as const

export type CampaignTab = (typeof TABS)[number]['key']

/**
 * A campaign's support page: what it is doing, then Team / Licenses /
 * Paperwork — the three things a customer calls about — and the way into its
 * workspace. The owner works in a campaign by putting himself on its team as
 * Consultant (2026-10-01): the customer sees who is helping, and the campaign
 * appears under My campaigns in the menu.
 */
export function PlatformCampaignPage() {
  const { campaignId } = useParams({ from: '/platform/campaigns/$campaignId' })
  const search = useSearch({ from: '/platform/campaigns/$campaignId' })
  const navigate = useNavigate()
  const { data: session } = useSession()
  const refreshSession = useRefreshSession()
  const client = useQueryClient()
  const campaigns = useAdminCampaigns()
  const today = useToday()
  const openWorkspace = useOpenWorkspace()
  const [note, setNote] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  const { error, setError, report } = useIssueState()
  const tab: CampaignTab = search.tab ?? 'team'
  const c = campaigns.data?.campaigns.find((x) => x.campaign_id === campaignId)
  const a = today.data?.activity.find((x) => x.campaign_id === campaignId)

  const userId = session?.state === 'authenticated' ? session.user_id : null
  const onTeam =
    session?.state === 'authenticated' &&
    session.grants.some((g) => g.scope_type === 'campaign' && g.scope_id === campaignId)

  const join = useMutation({
    mutationFn: () => apiPost(`/admin/users/${userId}/roles`, { role: 'consultant', scope_type: 'campaign', scope_id: campaignId }),
    onSuccess: async () => {
      setError(null)
      await refreshSession()
      await client.invalidateQueries({ queryKey: ['team', campaignId] })
      await client.invalidateQueries({ queryKey: ['admin-campaigns'] })
      await client.invalidateQueries({ queryKey: ['campaigns'] })
    },
    onError: report,
  })

  if (campaigns.isPending) return <p className="text-sm text-ink-muted">Loading…</p>
  if (!c) return <p className="text-sm text-ink-muted">No such campaign.</p>

  const setTab = (t: CampaignTab) => {
    setAdding(false)
    void navigate({ to: '/platform/campaigns/$campaignId', params: { campaignId }, search: { tab: t }, replace: true })
  }
  const now = (a?.calling_now ?? 0) + (a?.walking_now ?? 0)

  return (
    <div className="flex flex-col gap-5">
      <div>
        <p className="text-xs text-ink-muted">
          <Link to="/platform/customers" className="hover:underline">Customers</Link> ›{' '}
          <Link to="/platform/customers/$orgId" params={{ orgId: c.org_id }} className="hover:underline">{c.org_name}</Link> ›
        </p>
        <div className="mt-1 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold tracking-tight">{c.name}</h1>
            <p className="mt-1 flex flex-wrap items-center gap-2 text-sm text-ink-muted">
              <span>Election {shortDate(c.election_date)}</span>
              <Status c={c} />
            </p>
          </div>
          <button
            type="button"
            className={GHOST}
            onClick={() =>
              void openWorkspace(c.campaign_id).then((ok) => {
                if (!ok) setNote('This campaign cannot be worked in yet: it needs its signed attestation recorded and to be active.')
              })
            }
          >
            Open campaign tools ›
          </button>
        </div>
        {note ? <p role="status" className="mt-2 text-sm text-ink-muted">{note}</p> : null}
      </div>

      <div className="grid grid-cols-[repeat(auto-fit,minmax(11rem,1fr))] gap-3">
        <div className="rounded-lg border border-line bg-surface-raised p-4">
          <div className="text-xs text-ink-muted"><Live on={now > 0}>Right now</Live></div>
          <div className={`text-2xl font-semibold tabular-nums ${now ? 'text-brand-600' : ''}`}>{now}</div>
          <div className="text-xs text-ink-muted">{a?.calling_now ?? 0} calling · {a?.walking_now ?? 0} walking</div>
        </div>
        <div className="rounded-lg border border-line bg-surface-raised p-4">
          <div className="text-xs text-ink-muted">Results today</div>
          <div className="text-2xl font-semibold tabular-nums">{((a?.walk_today ?? 0) + (a?.phone_today ?? 0)).toLocaleString()}</div>
          <div className="mt-1"><Spark values={a?.trend ?? []} label="Results per day, last 14 days" /></div>
        </div>
        <div className="rounded-lg border border-line bg-surface-raised p-4">
          <div className="text-xs text-ink-muted">Team</div>
          <div className="text-2xl font-semibold tabular-nums">{c.people}</div>
          <div className="text-xs text-ink-muted">last active {ago(a?.last_active ?? null)}</div>
        </div>
      </div>

      <div role="tablist" className="flex flex-wrap gap-1 border-b border-line">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            onClick={() => setTab(t.key)}
            className={
              tab === t.key
                ? '-mb-px border-b-2 border-brand-700 px-3 py-2 text-sm font-semibold'
                : 'px-3 py-2 text-sm text-ink-muted hover:text-ink'
            }
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'team' ? (
        <div className="flex flex-col gap-3">
          <ErrorNote error={error} />
          {onTeam ? (
            <p className="text-sm text-ink-muted">You are on this team. It is in My campaigns in the menu.</p>
          ) : (
            <div className="flex flex-wrap items-center gap-3">
              <button type="button" className={GHOST} disabled={join.isPending || !userId} onClick={() => join.mutate()}>
                {join.isPending ? 'Adding…' : 'Add me as Consultant'}
              </button>
              <span className="text-xs text-ink-muted">To work in this campaign yourself. Its team will see you listed here.</span>
            </div>
          )}
          <TeamPanel campaignId={c.campaign_id} roleHere={isSuperAdmin(session) ? 'super_admin' : 'org_admin'} />
        </div>
      ) : null}
      {tab === 'licenses' ? (
        <div className="flex flex-col gap-4">
          <AgreementList campaignId={c.campaign_id} />
          {adding ? (
            <AddLicensesForm campaignId={c.campaign_id} onDone={() => setAdding(false)} />
          ) : (
            <div className="flex flex-wrap items-center gap-3">
              <button type="button" className={BUTTON} onClick={() => setAdding(true)}>Add licenses</button>
              <span className="text-xs text-ink-muted">
                Adds to the agreement they are already on, so the extra seats work right away.
              </span>
            </div>
          )}
        </div>
      ) : null}
      {tab === 'paperwork' ? (
        <div className="flex flex-col gap-6">
          <dl className="grid max-w-xl grid-cols-[max-content_1fr] gap-x-6 gap-y-2 text-sm">
            <dt className="text-ink-muted">Permitted-use attestation</dt>
            <dd>{c.attestation_document ?? <span className="text-ink-muted">not recorded</span>}</dd>
            <dt className="text-ink-muted">Signed</dt>
            <dd>{c.attestation_signed_at ? shortDate(c.attestation_signed_at.slice(0, 10)) : <span className="text-ink-muted">not signed</span>}</dd>
            <dt className="text-ink-muted">SOS vendor notice sent (§19005(c))</dt>
            <dd>{c.s19012_notified_at ? shortDate(c.s19012_notified_at.slice(0, 10)) : <span className="text-ink-muted">not recorded</span>}</dd>
            <dt className="text-ink-muted">Lists can be cut until</dt>
            <dd>{shortDate(c.cut_until)}</dd>
            <dt className="text-ink-muted">Reports open until</dt>
            <dd>{shortDate(c.report_until)}</dd>
          </dl>
          <AttestationForm c={c} />
        </div>
      ) : null}
    </div>
  )
}

/* ------------------------------------------------------------------ Billing */

const PAID_BY_LABEL: Record<string, string> = { check: 'Check', stripe: 'Card', comp: 'Comped', manual: 'Other' }

/**
 * Billing (2026-10-01: its own page, off the home screen). Every agreement and
 * its price, SuperAdmin only — Org Admins never receive prices from the
 * database, so the menu does not offer them this page.
 *
 * What has been paid is not tracked yet: payment_note is where a check number
 * goes today. Recording payments is its own piece of work.
 */
export function Billing() {
  const agreements = useAgreements()
  const customers = useCustomers()
  const { data: session } = useSession()
  const orgName = new Map((customers.data?.customers ?? []).map((c) => [c.org_id, c.name]))
  const rows = (agreements.data?.agreements ?? []).filter((a) => a.status === 'active')
  const booked = rows.reduce((n, a) => n + Number(a.price_cents ?? 0), 0)
  const unpriced = rows.filter((a) => a.price_cents === null).length

  if (!isSuperAdmin(session)) return <p className="text-sm text-ink-muted">Billing is for the SuperAdmin.</p>

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h1 className="text-xl font-semibold tracking-tight">Billing</h1>
        <p className="mt-1 max-w-prose text-sm text-ink-muted">
          Every live agreement and its price. Only you see this page; Org Admins see licenses, not prices.
        </p>
      </div>
      <div className="grid grid-cols-[repeat(auto-fit,minmax(11rem,1fr))] gap-3">
        <div className="rounded-lg border border-line bg-surface-raised p-4">
          <div className="text-xs text-ink-muted">Booked on live agreements</div>
          <div className="text-2xl font-semibold tabular-nums">{money(booked)}</div>
        </div>
        <div className="rounded-lg border border-line bg-surface-raised p-4">
          <div className="text-xs text-ink-muted">Agreements</div>
          <div className="text-2xl font-semibold tabular-nums">{rows.length}</div>
          <div className="text-xs text-ink-muted">{unpriced ? `${unpriced} with no price recorded` : 'all priced'}</div>
        </div>
      </div>
      <div className="overflow-x-auto rounded-lg border border-line">
        <table className="w-full min-w-[40rem] border-collapse text-sm">
          <thead className="bg-surface-sunken">
            <tr className="text-left text-xs tracking-wide text-ink-muted uppercase">
              <th scope="col" className="py-2 pr-4 pl-3 font-semibold">Agreement</th>
              <th scope="col" className="py-2 pr-4 font-semibold">Customer</th>
              <th scope="col" className="py-2 pr-4 font-semibold">Pays by</th>
              <th scope="col" className="py-2 pr-4 font-semibold">Note</th>
              <th scope="col" className="py-2 pr-3 text-right font-semibold">Price</th>
            </tr>
          </thead>
          <tbody>
            {agreements.isPending ? (
              <tr><td colSpan={5} className="px-3 py-4 text-ink-muted">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={5} className="px-3 py-6 text-ink-muted">No agreements yet.</td></tr>
            ) : (
              rows.map((a) => (
                <tr key={a.agreement_id} className="border-t border-line">
                  <td className="py-2 pr-4 pl-3">
                    <span className="font-semibold">{a.name}</span>
                    <div className="text-xs text-ink-muted">{a.basis === 'standard' ? 'Price list' : 'Custom deal'} · {a.campaigns.map((c) => c.name).join(', ')}</div>
                  </td>
                  <td className="py-2 pr-4">
                    <Link to="/platform/customers/$orgId" params={{ orgId: a.org_id }} className="hover:underline">
                      {orgName.get(a.org_id) ?? '…'}
                    </Link>
                  </td>
                  <td className="py-2 pr-4">{PAID_BY_LABEL[a.payment_source] ?? a.payment_source}</td>
                  <td className="py-2 pr-4 text-xs text-ink-muted">{a.payment_note ?? '—'}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{money(a.price_cents) ?? <span className="text-ink-muted">—</span>}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ One-off */

const LIST_TYPES = [
  { code: 'mail', label: 'Mail' },
  { code: 'walk', label: 'Walk' },
  { code: 'phone', label: 'Phone' },
]

/**
 * Quick one-off (2026-09-30): customer + campaign + purchase in one step, for
 * someone who needs a list and nothing else. Lands in the new campaign's
 * Universes page. The attestation is required here — the point is to cut a
 * list a minute later, and a list needs an attested campaign behind it.
 */
export function OneOff() {
  const client = useQueryClient()
  const { data: session } = useSession()
  const superAdmin = isSuperAdmin(session)
  const [price, setPrice] = useState('')
  const openWorkspace = useOpenWorkspace()
  const { error, setError, report } = useIssueState()
  const [customer, setCustomer] = useState('')
  const [f, setF] = useState({ name: '', election_date: '', attestation_document: '', attestation_signed_at: '', s19012_notified_at: '' })
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value })
  const [source, setSource] = useState('check')
  const [types, setTypes] = useState<string[]>(['mail'])
  const [limitCredits, setLimitCredits] = useState(true)
  const [credits, setCredits] = useState('2')
  const [note, setNote] = useState('')

  const create = useMutation({
    mutationFn: () =>
      apiPost<{ org_id: string; campaign_id: string }>('/admin/one-off', {
        customer_name: customer.trim() || f.name.trim(),
        name: f.name.trim(),
        election_date: f.election_date,
        attestation_document: f.attestation_document.trim(),
        attestation_signed_at: f.attestation_signed_at,
        s19012_notified_at: f.s19012_notified_at || null,
        source,
        list_type_codes: types.length === LIST_TYPES.length ? null : types,
        list_credits: limitCredits ? Number(credits) || 0 : null,
        note: note.trim() || null,
        price_cents: superAdmin ? dollarsToCents(price) : null,
      }),
    onSuccess: async (r) => {
      setError(null)
      await client.invalidateQueries({ queryKey: ['admin-customers'] })
      await client.invalidateQueries({ queryKey: ['admin-campaigns'] })
      await client.invalidateQueries({ queryKey: ['admin-agreements'] })
      await openWorkspace(r.campaign_id, '/universes')
    },
    onError: report,
  })

  const ready =
    f.name.trim() && f.election_date && f.attestation_document.trim() && f.attestation_signed_at && types.length > 0

  return (
    <div>
      <p className="text-xs text-ink-muted">
        <Link to="/platform/customers" className="hover:underline">Customers</Link> ›
      </p>
      <h1 className="mt-1 text-xl font-semibold tracking-tight">Quick one-off</h1>
      <p className="mt-1 max-w-prose text-sm text-ink-muted">
        For someone who needs a list and nothing else. Creates the customer, the campaign and the
        purchase together, then takes you to the campaign's universes to cut it.
      </p>

      <ErrorNote error={error} />

      <section className="mt-6 max-w-3xl">
        <label className="block text-sm">
          <span className={LABEL}>Customer (blank = same as the campaign)</span>
          <input id="oneoff-customer" value={customer} onChange={(e) => setCustomer(e.target.value)} maxLength={200} className={`w-full sm:w-96 ${INPUT}`} />
        </label>
        <CampaignFields f={f} set={set} />

        <div className="mt-5 flex flex-wrap items-end gap-5">
          <fieldset className="text-sm">
            <legend className={LABEL}>List types</legend>
            <div className="flex gap-3">
              {LIST_TYPES.map((t) => (
                <label key={t.code} className="flex items-center gap-1.5">
                  <input
                    type="checkbox"
                    checked={types.includes(t.code)}
                    onChange={(e) =>
                      setTypes(e.target.checked ? [...types, t.code] : types.filter((x) => x !== t.code))
                    }
                  />
                  {t.label}
                </label>
              ))}
            </div>
          </fieldset>
          <div className="text-sm">
            <label className="flex items-center gap-1.5">
              <input type="checkbox" checked={limitCredits} onChange={(e) => setLimitCredits(e.target.checked)} />
              <span>Limit to</span>
              <input
                id="oneoff-credits"
                type="number"
                min={0}
                value={credits}
                disabled={!limitCredits}
                onChange={(e) => setCredits(e.target.value)}
                className={`w-16 ${INPUT}`}
              />
              <span>lists</span>
            </label>
          </div>
          {superAdmin ? (
            <label className="text-sm">
              <span className={LABEL}>Price ($)</span>
              <input id="oneoff-price" inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="600" className={`w-28 ${INPUT}`} />
            </label>
          ) : null}
          <label className="text-sm">
            <span className={LABEL}>Paid by</span>
            <select id="oneoff-source" value={source} onChange={(e) => setSource(e.target.value)} className={INPUT}>
              <option value="check">Check</option>
              <option value="stripe">Card (Stripe)</option>
              <option value="comp">Comped</option>
              <option value="manual">Other</option>
            </select>
          </label>
        </div>
        <label className="mt-4 block text-sm">
          <span className={LABEL}>Note (optional)</span>
          <input id="oneoff-note" value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000} placeholder="Check #1042, $600" className={`w-full ${INPUT}`} />
        </label>

        <div className="mt-5 flex flex-wrap items-center gap-3">
          <button type="button" disabled={!ready || create.isPending} onClick={() => create.mutate()} className={BUTTON}>
            {create.isPending ? 'Setting up…' : 'Create and go to universes'}
          </button>
          <span className="text-xs text-ink-muted">
            Access runs to election day for cutting and 30 days after for reports.
          </span>
        </div>
      </section>
    </div>
  )
}

/* ------------------------------------------------------------------ Voter data */

/** The canonical layer's numbers and the snapshots behind them — platform business. */
export function VoterData() {
  return (
    <div className="space-y-10">
      <Overview />
      <Snapshots />
    </div>
  )
}

/* ------------------------------------------------------------------ Agreements */

function dollarsToCents(text: string): number | null {
  const n = Number(text.replace(/[$,\s]/g, ''))
  return text.trim() === '' || !Number.isFinite(n) ? null : Math.round(n * 100)
}

const PAID_BY: Record<string, string> = { check: 'check', stripe: 'card', comp: 'comped', manual: 'other' }

/** A pool meter: used of total, in the words the owner uses. */
function Meter({ used, total, noun }: { used: number; total: number | null; noun: string }) {
  if (total === 0) return null
  if (total === null) {
    return <div className="text-xs text-ink-muted">{noun}: not limited · {used} in use</div>
  }
  const pct = Math.min(100, Math.round((used / Math.max(total, 1)) * 100))
  return (
    <div className="text-xs">
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-brand-100">
        <div className="h-full bg-brand-600" style={{ width: `${pct}%` }} />
      </div>
      <div className="mt-0.5 text-ink-muted tabular-nums">
        {used} of {total} {noun}
      </div>
    </div>
  )
}

function AgreementList({ orgId, campaignId, campaigns }: { orgId?: string; campaignId?: string; campaigns?: AdminCampaign[] }) {
  const client = useQueryClient()
  const { error, setError, report } = useIssueState()
  const q = orgId ? `org_id=${orgId}` : `campaign_id=${campaignId}`
  const agreements = useQuery({
    queryKey: ['admin-agreements', q],
    queryFn: () => apiGet<{ agreements: Agreement[] }>(`/admin/agreements?${q}`),
  })
  const refresh = async () => {
    setError(null)
    await client.invalidateQueries({ queryKey: ['admin-agreements'] })
    await client.invalidateQueries({ queryKey: ['admin-campaigns'] })
    await client.invalidateQueries({ queryKey: ['campaigns'] })
  }
  const addCampaign = useMutation({
    mutationFn: ({ id, campaign }: { id: string; campaign: string }) =>
      apiPost(`/admin/agreements/${id}/campaigns`, { campaign_id: campaign }),
    onSuccess: refresh,
    onError: report,
  })
  const removeCampaign = useMutation({
    mutationFn: ({ id, campaign, reason }: { id: string; campaign: string; reason: string }) =>
      apiPost(`/admin/agreements/${id}/campaigns/${campaign}/remove`, { reason }),
    onSuccess: refresh,
    onError: report,
  })
  const voidIt = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => apiPost(`/admin/agreements/${id}/void`, { reason }),
    onSuccess: refresh,
    onError: report,
  })

  const rows = agreements.data?.agreements ?? []
  const manage = Boolean(orgId)

  return (
    <div>
      <ErrorNote error={error} />
      {agreements.isPending ? <p className="mt-3 text-sm text-ink-muted">Loading…</p> : null}
      {!agreements.isPending && rows.length === 0 ? (
        <p className="mt-3 text-sm text-ink-muted">No agreements yet.</p>
      ) : null}
      <div className="mt-3 grid gap-4 lg:grid-cols-2">
        {rows.map((a) => {
          const onIt = new Set(a.campaigns.map((c) => c.campaign_id))
          const addable = (campaigns ?? []).filter((c) => !onIt.has(c.campaign_id))
          const lists = a.list_type_codes === null ? 'all list types' : a.list_type_codes.join(', ')
          const voided = a.status === 'void'
          return (
            <section key={a.agreement_id} className={`flex min-w-0 flex-col gap-3 rounded-lg border border-line p-4 ${voided ? 'opacity-60' : 'bg-surface-raised'}`}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <h3 className="font-semibold leading-snug">{a.name}</h3>
                  <p className="text-xs text-ink-muted">
                    {a.package ? `${a.package} · ` : ''}
                    {a.basis === 'standard' ? 'standard price list' : 'custom deal'} · paid by {PAID_BY[a.payment_source] ?? a.payment_source}
                    {a.payment_note ? ` · ${a.payment_note}` : ''}
                  </p>
                </div>
                {money(a.price_cents) ? <span className="font-mono font-semibold tabular-nums">{money(a.price_cents)}</span> : null}
              </div>
              {voided ? <p className="text-xs">Void: {a.void_reason}</p> : null}

              <p className="text-sm">Lists ({lists}{a.list_credits !== null ? `, ${a.list_credits} allowed` : ''})</p>
              <div className="grid gap-2 sm:grid-cols-3">
                <Meter used={a.phones_in_use} total={a.dialer_seats} noun="phone seats in use" />
                <Meter used={a.walk_today} total={a.walk_licenses} noun="walk licenses today" />
                {a.list_credits !== null ? <Meter used={a.lists_used} total={a.list_credits} noun="lists cut" /> : null}
              </div>

              <ul className="space-y-1 text-sm">
                {a.campaigns.map((c) => (
                  <li key={c.campaign_id} className="grid grid-cols-[1fr_auto] items-start gap-x-3">
                    <span className="min-w-0">
                      {c.name}
                      <span className="block text-xs text-ink-muted tabular-nums">
                        cut to {c.cut_until} · reports to {c.report_until}
                      </span>
                    </span>
                    {manage && !voided ? (
                      <button
                        type="button"
                        className="text-xs text-ink-muted hover:text-ink"
                        onClick={() => {
                          const reason = prompt(`Why is ${c.name} coming off this agreement? (Its data stays with the campaign.)`)
                          if (reason?.trim()) removeCampaign.mutate({ id: a.agreement_id, campaign: c.campaign_id, reason: reason.trim() })
                        }}
                      >
                        Take off
                      </button>
                    ) : null}
                  </li>
                ))}
              </ul>

              {manage && !voided ? (
                <div className="flex flex-wrap items-center gap-3 border-t border-line pt-3">
                  {addable.length > 0 ? (
                    <select
                      aria-label="Add a campaign to this agreement"
                      className={INPUT}
                      value=""
                      onChange={(e) => e.target.value && addCampaign.mutate({ id: a.agreement_id, campaign: e.target.value })}
                    >
                      <option value="">Add a campaign…</option>
                      {addable.map((c) => (
                        <option key={c.campaign_id} value={c.campaign_id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  ) : null}
                  <button
                    type="button"
                    className="ml-auto text-xs text-ink-muted hover:text-ink"
                    onClick={() => {
                      const reason = prompt('Void this agreement? Every campaign on it loses what it gave them. Reason:')
                      if (reason?.trim()) voidIt.mutate({ id: a.agreement_id, reason: reason.trim() })
                    }}
                  >
                    Void agreement
                  </button>
                </div>
              ) : null}
            </section>
          )
        })}
      </div>
    </div>
  )
}

function NewAgreementForm({ orgId, campaigns, onDone }: { orgId: string; campaigns: AdminCampaign[]; onDone: () => void }) {
  const client = useQueryClient()
  const { data: session } = useSession()
  const superAdmin = isSuperAdmin(session)
  const { error, setError, report } = useIssueState()
  const [name, setName] = useState('')
  const [pkg, setPkg] = useState('')
  const [basis, setBasis] = useState<'custom' | 'standard'>('custom')
  const [price, setPrice] = useState('')
  const [source, setSource] = useState('check')
  const [paymentNote, setPaymentNote] = useState('')
  const [chosen, setChosen] = useState<string[]>([])
  const [types, setTypes] = useState<string[]>(LIST_TYPES.map((t) => t.code))
  const [limitLists, setLimitLists] = useState(false)
  const [lists, setLists] = useState('5')
  const [seats, setSeats] = useState('0')
  const [walkMode, setWalkMode] = useState<'none' | 'count' | 'unlimited'>('count')
  const [walk, setWalk] = useState('5')
  const [accessFrom, setAccessFrom] = useState('')
  const [endsOn, setEndsOn] = useState('')

  const create = useMutation({
    mutationFn: () =>
      apiPost('/admin/agreements', {
        org_id: orgId,
        name: name.trim(),
        package: pkg.trim() || null,
        basis,
        price_cents: superAdmin ? dollarsToCents(price) : null,
        payment_source: source,
        payment_note: paymentNote.trim() || null,
        access_from: accessFrom || null,
        ends_on: endsOn || null,
        list_type_codes: types.length === LIST_TYPES.length ? null : types,
        list_credits: limitLists ? Number(lists) || 0 : null,
        dialer_seats: Number(seats) || 0,
        walk_licenses: walkMode === 'none' ? 0 : walkMode === 'unlimited' ? null : Number(walk) || 0,
        campaign_ids: chosen,
      }),
    onSuccess: async () => {
      setError(null)
      setName('')
      setPkg('')
      setPrice('')
      setPaymentNote('')
      setChosen([])
      await client.invalidateQueries({ queryKey: ['admin-agreements'] })
      await client.invalidateQueries({ queryKey: ['admin-campaigns'] })
      await client.invalidateQueries({ queryKey: ['campaigns'] })
      onDone()
    },
    onError: report,
  })

  if (campaigns.length === 0) return null

  return (
    <section className="rounded-lg border border-brand-200 bg-brand-50 p-4">
      <h2 className="text-sm font-semibold">New agreement</h2>
      <ErrorNote error={error} />

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <label className="text-sm">
          <span className={LABEL}>Name</span>
          <input id="ag-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={200} placeholder="2026 General — three races" className={`w-full ${INPUT}`} />
        </label>
        <label className="text-sm">
          <span className={LABEL}>Package (optional)</span>
          <input id="ag-package" value={pkg} onChange={(e) => setPkg(e.target.value)} maxLength={200} placeholder="Lists + walk ×3" className={`w-full ${INPUT}`} />
        </label>
      </div>

      <fieldset className="mt-4 text-sm">
        <legend className={LABEL}>Campaigns it covers — licenses are shared between them</legend>
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          {campaigns.map((c) => (
            <label key={c.campaign_id} className="flex items-center gap-1.5">
              <input
                type="checkbox"
                checked={chosen.includes(c.campaign_id)}
                onChange={(e) =>
                  setChosen(e.target.checked ? [...chosen, c.campaign_id] : chosen.filter((x) => x !== c.campaign_id))
                }
              />
              {c.name}
            </label>
          ))}
        </div>
      </fieldset>

      <div className="mt-4 flex flex-wrap items-end gap-5">
        <fieldset className="text-sm">
          <legend className={LABEL}>List types</legend>
          <div className="flex gap-3">
            {LIST_TYPES.map((t) => (
              <label key={t.code} className="flex items-center gap-1.5">
                <input
                  type="checkbox"
                  checked={types.includes(t.code)}
                  onChange={(e) => setTypes(e.target.checked ? [...types, t.code] : types.filter((x) => x !== t.code))}
                />
                {t.label}
              </label>
            ))}
          </div>
        </fieldset>
        <label className="flex items-center gap-1.5 text-sm">
          <input type="checkbox" checked={limitLists} onChange={(e) => setLimitLists(e.target.checked)} />
          <span>Limit to</span>
          <input id="ag-lists" type="number" min={0} value={lists} disabled={!limitLists} onChange={(e) => setLists(e.target.value)} className={`w-16 ${INPUT}`} />
          <span>lists in all</span>
        </label>
      </div>

      <div className="mt-4 flex flex-wrap items-end gap-5">
        <label className="text-sm">
          <span className={LABEL}>Phone seats (at once)</span>
          <input id="ag-seats" type="number" min={0} value={seats} onChange={(e) => setSeats(e.target.value)} className={`w-20 ${INPUT}`} />
        </label>
        <fieldset className="text-sm">
          <legend className={LABEL}>Walk app (phones per day)</legend>
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-1.5">
              <input type="radio" name="ag-walk" checked={walkMode === 'none'} onChange={() => setWalkMode('none')} />
              None
            </label>
            <label className="flex items-center gap-1.5">
              <input type="radio" name="ag-walk" checked={walkMode === 'count'} onChange={() => setWalkMode('count')} />
              <input id="ag-walk" type="number" min={0} value={walk} disabled={walkMode !== 'count'} onChange={(e) => setWalk(e.target.value)} className={`w-16 ${INPUT}`} />
            </label>
            <label className="flex items-center gap-1.5">
              <input type="radio" name="ag-walk" checked={walkMode === 'unlimited'} onChange={() => setWalkMode('unlimited')} />
              Not limited
            </label>
          </div>
        </fieldset>
      </div>

      <div className="mt-4 flex flex-wrap items-end gap-5">
        {superAdmin ? (
          <label className="text-sm">
            <span className={LABEL}>Price ($)</span>
            <input id="ag-price" inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="60000" className={`w-32 ${INPUT}`} />
          </label>
        ) : null}
        <label className="text-sm">
          <span className={LABEL}>Pricing</span>
          <select id="ag-basis" value={basis} onChange={(e) => setBasis(e.target.value as 'custom' | 'standard')} className={INPUT}>
            <option value="custom">Custom deal</option>
            <option value="standard">Standard price list</option>
          </select>
        </label>
        <label className="text-sm">
          <span className={LABEL}>Paid by</span>
          <select id="ag-source" value={source} onChange={(e) => setSource(e.target.value)} className={INPUT}>
            <option value="check">Check</option>
            <option value="stripe">Card (Stripe)</option>
            <option value="comp">Comped</option>
            <option value="manual">Other</option>
          </select>
        </label>
        <label className="min-w-[14rem] flex-1 text-sm">
          <span className={LABEL}>Payment note</span>
          <input id="ag-payment-note" value={paymentNote} onChange={(e) => setPaymentNote(e.target.value)} maxLength={2000} placeholder="Check #1042, half now, half Oct 1" className={`w-full ${INPUT}`} />
        </label>
      </div>

      <div className="mt-4 flex flex-wrap items-end gap-5">
        <label className="text-sm">
          <span className={LABEL}>Starts (optional)</span>
          <input id="ag-from" type="date" value={accessFrom} onChange={(e) => setAccessFrom(e.target.value)} className={INPUT} />
        </label>
        <label className="text-sm">
          <span className={LABEL}>Ends (optional)</span>
          <input id="ag-ends" type="date" value={endsOn} onChange={(e) => setEndsOn(e.target.value)} className={INPUT} />
        </label>
        <p className="max-w-sm text-xs text-ink-muted">
          Blank uses each campaign's own election: open 120 days before, cutting stops on election
          day, reports 30 days after. An end date caps all of them.
        </p>
      </div>

      <div className="mt-4">
        <button
          type="button"
          disabled={!name.trim() || chosen.length === 0 || types.length === 0 || create.isPending}
          onClick={() => create.mutate()}
          className={BUTTON}
        >
          {create.isPending ? 'Saving…' : 'Record agreement'}
        </button>
        <button type="button" className="ml-3 text-sm text-ink-muted hover:text-ink" onClick={onDone}>Cancel</button>
      </div>
    </section>
  )
}

/**
 * Add licenses to the agreement a campaign is on (0095). A campaign on more
 * than one agreement picks which; on a shared agreement every campaign on it
 * gets the extra licenses, and the form says so.
 */
function AddLicensesForm({ campaignId, onDone }: { campaignId: string; onDone: () => void }) {
  const client = useQueryClient()
  const { data: session } = useSession()
  const superAdmin = isSuperAdmin(session)
  const agreements = useAgreements({ campaignId })
  const { error, setError, report } = useIssueState()
  const live = (agreements.data?.agreements ?? []).filter((a) => a.status === 'active')
  const [picked, setPicked] = useState('')
  const [seats, setSeats] = useState('0')
  const [walk, setWalk] = useState('0')
  const [lists, setLists] = useState('0')
  const [price, setPrice] = useState('')
  const [note, setNote] = useState('')
  const a: Agreement | undefined = live.find((x) => x.agreement_id === picked) ?? live[0]

  const save = useMutation({
    mutationFn: () =>
      apiPost(`/admin/agreements/${a!.agreement_id}/licenses`, {
        dialer_seats: Number(seats) || 0,
        walk_licenses: Number(walk) || 0,
        list_credits: Number(lists) || 0,
        price_cents: superAdmin ? dollarsToCents(price) : null,
        note: note.trim() || null,
      }),
    onSuccess: async () => {
      setError(null)
      await client.invalidateQueries({ queryKey: ['admin-agreements'] })
      await client.invalidateQueries({ queryKey: ['admin-campaigns'] })
      await client.invalidateQueries({ queryKey: ['campaigns'] })
      onDone()
    },
    onError: report,
  })

  if (agreements.isPending) return <p className="text-sm text-ink-muted">Loading…</p>
  if (!a) {
    return (
      <p className="text-sm text-ink-muted">
        This campaign is not on an agreement yet. Record one on the customer's page first.
      </p>
    )
  }
  const shared = a.campaigns.filter((x) => x.campaign_id !== campaignId)
  const any = (Number(seats) || 0) + (Number(walk) || 0) + (Number(lists) || 0) > 0

  return (
    <section className="flex flex-col gap-3 rounded-lg border border-brand-200 bg-brand-50 p-4">
      <h2 className="text-sm font-semibold">Add licenses to {a.name}</h2>
      <ErrorNote error={error} />
      {live.length > 1 ? (
        <label className="text-sm">
          <span className={LABEL}>Agreement</span>
          <select id="lic-agreement" value={a.agreement_id} onChange={(e) => setPicked(e.target.value)} className={INPUT}>
            {live.map((x) => <option key={x.agreement_id} value={x.agreement_id}>{x.name}</option>)}
          </select>
        </label>
      ) : null}
      <div className="flex flex-wrap items-end gap-4">
        <label className="text-sm">
          <span className={LABEL}>More phone seats</span>
          <input id="lic-seats" type="number" min={0} value={seats} onChange={(e) => setSeats(e.target.value)} className={`w-24 ${INPUT}`} />
        </label>
        <label className="text-sm">
          <span className={LABEL}>More walk licenses</span>
          {a.walk_licenses === null ? (
            <span className="block py-1.5 text-sm text-ink-muted">Not limited</span>
          ) : (
            <input id="lic-walk" type="number" min={0} value={walk} onChange={(e) => setWalk(e.target.value)} className={`w-24 ${INPUT}`} />
          )}
        </label>
        <label className="text-sm">
          <span className={LABEL}>More lists</span>
          {a.list_credits === null ? (
            <span className="block py-1.5 text-sm text-ink-muted">Not limited</span>
          ) : (
            <input id="lic-lists" type="number" min={0} value={lists} onChange={(e) => setLists(e.target.value)} className={`w-24 ${INPUT}`} />
          )}
        </label>
        {superAdmin ? (
          <label className="text-sm">
            <span className={LABEL}>Add to price ($)</span>
            <input id="lic-price" inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="800" className={`w-28 ${INPUT}`} />
          </label>
        ) : null}
        <label className="min-w-[12rem] flex-1 text-sm">
          <span className={LABEL}>Note (optional)</span>
          <input id="lic-note" value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000} placeholder="2 more seats for GOTV, check to follow" className={`w-full ${INPUT}`} />
        </label>
      </div>
      <p className="text-xs text-ink-muted">
        {shared.length > 0
          ? `This agreement is shared with ${shared.map((x) => x.name).join(', ')}. They all draw from the added licenses.`
          : 'They apply as soon as you add them.'}
      </p>
      <div className="flex items-center gap-3">
        <button type="button" disabled={!any || save.isPending} onClick={() => save.mutate()} className={BUTTON}>
          {save.isPending ? 'Adding…' : 'Add licenses'}
        </button>
        <button type="button" className="text-sm text-ink-muted hover:text-ink" onClick={onDone}>Cancel</button>
      </div>
    </section>
  )
}

/**
 * Record the signed attestation after the campaign exists (0089). Also how a
 * mistake is corrected — the audit row keeps what it replaced.
 */
function AttestationForm({ c }: { c: AdminCampaign }) {
  const client = useQueryClient()
  const { error, setError, report } = useIssueState()
  const [doc, setDoc] = useState(c.attestation_document ?? '')
  const [signed, setSigned] = useState(c.attestation_signed_at?.slice(0, 10) ?? '')
  const [notified, setNotified] = useState(c.s19012_notified_at?.slice(0, 10) ?? '')
  const [saved, setSaved] = useState(false)

  const save = useMutation({
    mutationFn: () =>
      apiPost(`/admin/campaigns/${c.campaign_id}/attestation`, {
        attestation_document: doc.trim(),
        attestation_signed_at: signed,
        s19012_notified_at: notified || null,
      }),
    onSuccess: async () => {
      setError(null)
      setSaved(true)
      await client.invalidateQueries({ queryKey: ['admin-campaigns'] })
      await client.invalidateQueries({ queryKey: ['campaigns'] })
    },
    onError: (e) => {
      setSaved(false)
      report(e)
    },
  })

  const already = Boolean(c.attestation_signed_at)
  return (
    <section className="max-w-3xl rounded-lg border border-line bg-surface-sunken p-4">
      <h2 className="text-sm font-semibold">{already ? 'Correct the attestation' : 'Record the signed attestation'}</h2>
      <p className="mt-1 text-xs text-ink-muted">
        {already
          ? 'Changing it is recorded in the audit trail with the values it replaced.'
          : 'Until this is recorded, nobody can work in this campaign.'}
      </p>
      <ErrorNote error={error} />
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <label className="text-sm sm:col-span-2">
          <span className={LABEL}>Where the signed form is filed</span>
          <input id="att-doc" value={doc} onChange={(e) => { setDoc(e.target.value); setSaved(false) }} maxLength={500} placeholder="Drive: Attestations/2026/alvarez.pdf" className={`w-full ${INPUT}`} />
        </label>
        <label className="text-sm">
          <span className={LABEL}>Signed on</span>
          <input id="att-signed" type="date" value={signed} onChange={(e) => { setSigned(e.target.value); setSaved(false) }} className={`w-full ${INPUT}`} />
        </label>
        <label className="text-sm">
          <span className={LABEL}>SOS vendor notice sent on, §19005(c) (optional)</span>
          <input id="att-19012" type="date" value={notified} onChange={(e) => { setNotified(e.target.value); setSaved(false) }} className={`w-full ${INPUT}`} />
        </label>
      </div>
      <div className="mt-3 flex items-center gap-3">
        <button type="button" disabled={!doc.trim() || !signed || save.isPending} onClick={() => save.mutate()} className={BUTTON}>
          {save.isPending ? 'Saving…' : already ? 'Save correction' : 'Record attestation'}
        </button>
        {saved ? <span role="status" className="text-sm text-ink-muted">Saved.</span> : null}
      </div>
    </section>
  )
}
